// Oscar the Overtaker — pure helpers (no React, no app deps) for the Kalshi
// overtake/blocker flow in GameDetailPanel's OrderEntryForm. Parsing /
// validation / preview live here so they're unit-testable; the component owns
// form state and the fire call (accountStore.placeOrderSequence → POST
// /api/kalshi/order-sequence), and ./oscarResultPresentation owns what the
// ticket says once that call returns.
//
// The blocker convention: taking price P is protected by an OPPOSITE-side bid
// at 99 − P (sum-to-99; one level ahead on the take side). The relay is the
// authority — it recomputes the blocker price server-side (relay/src/kalshi/
// oscar.ts) — so everything here is preview/display only.

import type { KalshiOrder, OscarSequenceInput } from '../../types/kalshi';
import { blockerCents, mirrorCents } from '../../lib/pricing/subCent';

export type OscarPreviewLeg = {
  role: 'cancel' | 'take' | 'blocker';
  side: 'yes' | 'no';
  priceCents: number;
  qty: number;
};

export type OscarPreview =
  | { ok: true; legs: OscarPreviewLeg[]; blockerPriceCents: number | null }
  | { ok: false; error: string };

/** Matches the relay's oscarCoid(sequenceId, 'blocker') scheme. */
// Accept both the original 8-hex form (existing blockers already resting at
// deploy time) and the full-UUID retry-safe form.
const OSCAR_BLOCKER_COID_RE = /^osc-(?:[0-9a-f]{8}|[0-9a-f-]{36})-blk$/;

/** A resting order the oscar sequence placed as a blocker leg. Depends on
 *  Kalshi echoing client_order_id on order reads (smoke-validated). */
export function isOscarBlockerOrder(order: Pick<KalshiOrder, 'clientOrderId'>): boolean {
  return OSCAR_BLOCKER_COID_RE.test(order.clientOrderId ?? '');
}

export interface OscarRetryIdentity {
  ownerId: string;
  signature: string;
  sequenceId: string;
  createdAtMs: number;
}

const OSCAR_RETRY_STORAGE_KEY = 'g1.oscar.pending-sequence.v1';
export const OSCAR_RETRY_TTL_MS = 10 * 60 * 1_000;

/** One UUID belongs to one exact logical fire. An ambiguous or partial result
 * reuses it; any edit to the form creates a new venue identity. */
export function oscarRetryIdentity(
  previous: OscarRetryIdentity | null,
  input: Omit<OscarSequenceInput, 'sequenceId' | 'retry'>,
  createId: () => string,
  ownerId: string,
  nowMs = Date.now(),
): OscarRetryIdentity {
  const signature = JSON.stringify([
    input.op,
    input.ticker,
    input.side,
    input.take.priceCents,
    input.take.count,
    input.blocker?.count ?? null,
    input.expirationTs ?? null,
    input.cancelOrderId ?? null,
  ]);
  return previous?.ownerId === ownerId &&
    previous.signature === signature &&
    nowMs - previous.createdAtMs <= OSCAR_RETRY_TTL_MS
    ? previous
    : { ownerId, signature, sequenceId: createId(), createdAtMs: nowMs };
}

/** Session storage survives a refresh in the same trading tab, covering the
 * most dangerous ambiguous-response recovery without turning an old identical
 * order into a retry forever. */
export function loadOscarRetryIdentity(ownerId: string): OscarRetryIdentity | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = JSON.parse(sessionStorage.getItem(OSCAR_RETRY_STORAGE_KEY) ?? 'null') as unknown;
    if (!raw || typeof raw !== 'object') return null;
    const value = raw as Record<string, unknown>;
    if (
      typeof value.signature !== 'string' ||
      typeof value.sequenceId !== 'string' ||
      typeof value.createdAtMs !== 'number' ||
      value.ownerId !== ownerId ||
      Date.now() - value.createdAtMs > OSCAR_RETRY_TTL_MS
    ) {
      sessionStorage.removeItem(OSCAR_RETRY_STORAGE_KEY);
      return null;
    }
    return value as unknown as OscarRetryIdentity;
  } catch {
    return null;
  }
}

export function saveOscarRetryIdentity(identity: OscarRetryIdentity | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (identity == null) sessionStorage.removeItem(OSCAR_RETRY_STORAGE_KEY);
    else sessionStorage.setItem(OSCAR_RETRY_STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Storage denial must not disable trading; the in-memory ref still covers
    // retries for the lifetime of the mounted tab.
  }
}

export interface OscarPreviewInput {
  /** Take-leg side — the ticket's YES/NO. */
  side: 'yes' | 'no';
  /** Raw price input string, cents. */
  takePriceRaw: string;
  /** Raw qty input string, whole contracts. */
  takeQtyRaw: string;
  blockerOn: boolean;
  blockerQtyRaw: string;
  /** Release mode: the resting blocker to cancel first. */
  cancelOrderId?: string | null;
}

/**
 * Validate the oscar inputs and expand the leg preview the ticket renders
 * inline (the preview IS the confirmation — no modal in the hot path).
 * Whole-cent 1..98 only, mirroring the relay's V1 sub-cent rejection.
 */
export function computeOscarPreview(input: OscarPreviewInput): OscarPreview {
  const priceCents = Number(input.takePriceRaw);
  if (
    input.takePriceRaw.trim() === '' ||
    !Number.isInteger(priceCents) ||
    priceCents < 1 ||
    priceCents > 98
  ) {
    return { ok: false, error: 'Overtake price must be a whole 1–98¢' };
  }
  const qty = Number(input.takeQtyRaw);
  if (input.takeQtyRaw.trim() === '' || !Number.isInteger(qty) || qty < 1) {
    return { ok: false, error: 'Qty must be a whole number ≥ 1' };
  }
  const releaseMode = input.cancelOrderId != null && input.cancelOrderId !== '';
  // Release & Overtake removes the old blocker, but a replacement remains an
  // independent trader choice. The accepted contract must not silently turn
  // the attach-blocker leg on.
  const withBlocker = input.blockerOn;
  let blockerQty = 0;
  if (withBlocker) {
    blockerQty = Number(input.blockerQtyRaw);
    if (
      input.blockerQtyRaw.trim() === '' ||
      !Number.isInteger(blockerQty) ||
      blockerQty < 1
    ) {
      return { ok: false, error: 'Blocker qty must be a whole number ≥ 1' };
    }
  }

  const blockerSide = input.side === 'yes' ? 'no' : 'yes';
  const oppositePriceCents = blockerCents(priceCents);
  const blockerPriceCents = withBlocker ? oppositePriceCents : null;
  const legs: OscarPreviewLeg[] = [];
  if (releaseMode) {
    legs.push({ role: 'cancel', side: blockerSide, priceCents: oppositePriceCents, qty: 0 });
  }
  legs.push({ role: 'take', side: input.side, priceCents, qty });
  if (withBlocker && blockerPriceCents != null) {
    legs.push({ role: 'blocker', side: blockerSide, priceCents: blockerPriceCents, qty: blockerQty });
  }
  return { ok: true, legs, blockerPriceCents };
}

export interface OscarReleasePrefill {
  cancelOrderId: string;
  /** The NEW take side = opposite of the blocker's side. */
  takeSide: 'yes' | 'no';
  /** The level the blocker was protecting (involution: 99 − blocker price). */
  takePriceCents: number;
}

/**
 * Derive the release-and-overtake prefill from a resting blocker order. The
 * blocker rested at price p on side s ⇒ the level it protects is 99 − p on
 * the opposite side — where the trader wants the new take. Side-native price
 * uses the same mirrorCents yes_price fallback as GameOpenOrders (Kalshi
 * often returns only yes_price on NO orders). Null when the order carries no
 * usable price or the derived level is outside 1..98.
 */
export function releasePrefillFromBlocker(
  order: Pick<KalshiOrder, 'orderId' | 'side' | 'yesPrice' | 'noPrice'>,
): OscarReleasePrefill | null {
  const sidePrice =
    order.side === 'yes'
      ? order.yesPrice ?? (order.noPrice != null ? mirrorCents(order.noPrice) : null)
      : order.noPrice ?? (order.yesPrice != null ? mirrorCents(order.yesPrice) : null);
  if (sidePrice == null) return null;
  const takePriceCents = blockerCents(sidePrice);
  if (!Number.isInteger(takePriceCents) || takePriceCents < 1 || takePriceCents > 98) {
    return null;
  }
  return {
    cancelOrderId: order.orderId,
    takeSide: order.side === 'yes' ? 'no' : 'yes',
    takePriceCents,
  };
}

// #474 — the sequence RESULT description moved to ./oscarResultPresentation,
// which types the severity and wording class the ticket's result banner needs.
// This module stays the pre-fire side: preview, validation, retry identity and
// blocker/prefill derivation.
